import { useState, useEffect } from "react";
import { Box, Dialog, Divider, Slide, Typography, Button, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { forwardRef } from "react";
import { VERIFICATION_CHECKLISTS, getVerificationTitle, getVerificationDescription } from "./verificationChecklists";
import { useDarkMode } from "../useDarkMode";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function VerificationDialog({
  open,
  onClose,
  onSubmit,
  verificationType,
  isSaving
}) {
  const [checkedItems, setCheckedItems] = useState([]);
  const [allChecked, setAllChecked] = useState(false);
  const isDarkMode = useDarkMode();

  // Handle checkbox change
  const handleCheckboxChange = (index) => (event) => {
    const newCheckedItems = [...checkedItems];
    newCheckedItems[index] = event.target.checked;
    setCheckedItems(newCheckedItems);
    setAllChecked(newCheckedItems.every(item => item === true));
  };

  // Reset the checklist when verification type changes
  useEffect(() => {
    if (verificationType) {
      const checklist = VERIFICATION_CHECKLISTS[verificationType] || [];
      setCheckedItems(Array(checklist.length).fill(false));
      setAllChecked(false);
    }
  }, [verificationType]);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="verification-dialog-description"
      fullWidth
      maxWidth="md"
      scroll="paper"
      sx={{
        "& .MuiPaper-root": {
          bgcolor: isDarkMode ? "#26282C" : "#F6F6F6",
          borderRadius: "12px",
          color: isDarkMode ? "#878D98" : "#667085",
          maxHeight: "90vh"
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {getVerificationTitle(verificationType)}
        </Typography>

        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon sx={{ color: isDarkMode ? "#878D98" : "#667085" }} />
        </IconButton>
      </Box>

      <Divider sx={{ bgcolor: isDarkMode ? "#878D98" : "#667085" }} />

      <Box sx={{ p: 3, maxHeight: "60vh", overflow: "auto" }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {getVerificationDescription(verificationType)}
        </Typography>

        <FormGroup>
          {(VERIFICATION_CHECKLISTS[verificationType] || []).map((item, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={checkedItems[index] || false}
                  sx={{
                    color: isDarkMode ? "#878D98" : "#667085",
                  }}
                  onChange={handleCheckboxChange(index)}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: "14px" }}>
                  {/* Replace simple text with formatted text */}
                  {typeof item === 'string'
                    ? item
                    : (
                      <>
                        <span style={{ fontWeight: 'bold' }}>{item.boldText}</span>
                        : {item.text}
                      </>
                    )}
                </Typography>
              }
              sx={{ mb: 1, flex: 1 }}
            />
          ))}
        </FormGroup>
      </Box>

      <Divider />
      {/* Error message on top when not all items are checked */}
      {!allChecked && (
        <Box
          sx={{
            mt: 2,
            mx: 3,
            p: 2,
            bgcolor: isDarkMode ? "#422006" : "#fefce8",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color={isDarkMode ? "#EAB308" : "error"}>
            Please check all items before proceeding. All checkboxes must be selected to enable submission.
          </Typography>
        </Box>
      )}
      <Box sx={{ p: 3, display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          disabled={!allChecked}
          loading={isSaving}
          onClick={() => onSubmit()}
        >
          Submit
        </LoadingButton>
      </Box>
    </Dialog>
  );
}
